"use client";

import { useCallback } from 'react';
import { useLanguage } from '@/contexts/language-context';
import { getTranslation } from '@/lib/translations';
import type { TranslationKey } from '@/types/translations';
import { PreferredLanguage } from '@/models/enum';

/**
 * Hook for accessing translations in React components
 * @returns An object with the current language, translation function, and language setter
 */
export function useTranslation() {
  const { language, setLanguage } = useLanguage();

  /**
   * Get a translation for the current language
   * @param key - The translation key in dot notation
   * @param variables - Variables to interpolate into the translation
   * @returns The translated string
   */
  const t = useCallback(
    (key: TranslationKey, variables?: Record<string, any>): string => {
      return getTranslation(key, language, variables);
    },
    [language]
  );

  /**
   * Get a translation for a specific language (useful for language switching preview)
   * @param key - The translation key in dot notation
   * @param targetLanguage - The target language
   * @param variables - Variables to interpolate into the translation
   * @returns The translated string
   */
  const tLang = useCallback(
    (key: TranslationKey, targetLanguage: PreferredLanguage, variables?: Record<string, any>): string => {
      return getTranslation(key, targetLanguage, variables);
    },
    []
  );

  return {
    language: language,
    setLanguage,
    t,
    tLang,
  };
}

export default useTranslation; 